import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Progress,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import BusinessAuth from "layouts/auth/Business";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { axiosService } from "network/axios";
import { setAlertMessageRequest } from "store/ui/reducer";
import { getUserRequest } from "store/user/reducer";
import { isLoadingUserSelector } from "store/user/selector";
import { userSelector } from "store/user/selector";
import { IconRoundedCheck } from "components/icons/Icons";
import { VerifyEmail, EmailVerified, BusinessInfo, FinalizeAccount } from "./components";
import { useBusinessAutologin } from "./hooks/useBusinessAutologin";
import { useBusinessStripePayment } from "./hooks/useBusinessStripePayment";
import { clearDraftStorage } from "views/customer/order/utils/draftStorage";

type StepKey = 1 | 2 | 3 | 4;

export default function SignUpBusiness() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialEmail =
    ((location.state as { email?: string } | null)?.email ||
      searchParams.get("email") ||
      "") as string;
  const userId = searchParams.get("user_id");
  const token = searchParams.get("token");

  const [currentStep, setCurrentStep] = useState<StepKey>(1);
  const [email] = useState(initialEmail);
  const [businessName, setBusinessName] = useState("");
  const [industry, setIndustry] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [verificationInfo, setVerificationInfo] = useState<string | null>(null);
  const [isBackendVerified, setIsBackendVerified] = useState(false);
  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(false);
  const [teamMembers, setTeamMembers] = useState([
    { email: "", role: "Team member", emailError: "" },
  ]);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [isCompletingSetup, setIsCompletingSetup] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [teamMemberError, setTeamMemberError] = useState<string | null>(null);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const bgColor = useColorModeValue("#FFF", "navy.800");
  const user = useSelector(userSelector);
  const isLoggingIn = useSelector(isLoadingUserSelector);

  const totalSteps = 4 as const;
  const progressPercentage = (currentStep / totalSteps) * 100;
  const { autologin } = useBusinessAutologin();

  // Listen for successful login and transition to step 3
  useEffect(() => {
    if (user && currentStep === 1 && token && userId) {
      sessionStorage.removeItem("pending_auth");
      setCurrentStep(3);
    }
  }, [user, currentStep, token, userId]);

  useEffect(() => {
    const isVerifyRoute = location.pathname === "/auth/verify";
    if (!isVerifyRoute) return;

    if (!userId || !token) {
      setCurrentStep(1);
      return;
    }

    let cancelled = false;
    setCurrentStep(2);
    setVerificationError(null);
    setVerificationInfo(null);
    setIsBackendVerified(false);
    const verify = async () => {
      try {
        setIsVerifying(true);
        await axiosService.get(
          `/user/verify/${userId}?token=${encodeURIComponent(token)}`
        );
        if (!cancelled) {
          setIsBackendVerified(true);
        }
      } catch (err: any) {
        const message =
          err?.data?.messages?.error ||
          err?.response?.data?.messages?.error ||
          "Failed to verify your account. Please try again.";
        if (!cancelled) {
          setVerificationError(message);
          setIsBackendVerified(false);
        }
      } finally {
        if (!cancelled) {
          setIsVerifying(false);
        }
      }
    };

    verify();

    return () => {
      cancelled = true;
    };
  }, [dispatch, location.pathname, token, userId]);

  const {
    cardHolderName,
    setCardHolderName,
    cardBrand,
    isCardNumberFocused,
    isCardExpiryFocused,
    isCardCvcFocused,
    isSavingCardInfo,
    handleCompleteSetup,
  } = useBusinessStripePayment({ currentStep, paymentMethod, userId });

  const handleNext = useCallback(async () => {
    if (currentStep === 1) {
      setVerificationError(null);
      setVerificationInfo(null);
      setIsBackendVerified(false);

      if (!token) {
        setVerificationInfo(
          "Waiting for email verification. Please check your inbox and open the verification link."
        );
        return;
      }

      setCurrentStep(2);

      if (!userId) {
        setVerificationError("Unable to verify your email. Missing user id.");
        return;
      }

      let cancelled = false;
      try {
        setIsVerifying(true);
        await axiosService.get(
          `/user/verify/${userId}?token=${encodeURIComponent(token)}`
        );
        if (!cancelled) {
          setIsBackendVerified(true);
        }
      } catch (err: any) {
        const message =
          err?.data?.messages?.error ||
          err?.response?.data?.messages?.error ||
          "Failed to verify your account. Please try again.";
        if (!cancelled) {
          setVerificationError(message);
          setIsBackendVerified(false);
        }
      } finally {
        if (!cancelled) {
          setIsVerifying(false);
        }
      }
      return;
    }

    if (currentStep < totalSteps) {
      setCurrentStep((currentStep + 1) as StepKey);
    }
  }, [currentStep, token, userId]);

  const handleBack = useCallback(() => {
    if (currentStep === 1 || currentStep === 2 || currentStep === 3) {
      localStorage.removeItem("access_token");
      localStorage.removeItem("user_details");
      sessionStorage.removeItem("pending_auth");
      navigate("/auth/sign-in");
      return;
    }

    if (currentStep > 3) {
      setCurrentStep((currentStep - 1) as StepKey);
    }
  }, [currentStep, navigate]);

  const handleContinue = useCallback(async () => {
    if (!isBackendVerified) return;
    if (isAutoLoggingIn) return;

    try {
      setIsAutoLoggingIn(true);
      const res = await autologin(userId);
      if (res.success) {
        // Fetch user data and store in Redux
        dispatch(getUserRequest({ user_id: Number(userId) }));
        setCurrentStep(3);
      } else {
        setVerificationError(res.error || "Auto-login failed. Please login manually.");
      }
    } finally {
      setIsAutoLoggingIn(false);
    }
  }, [autologin, dispatch, isAutoLoggingIn, isBackendVerified, userId]);

  const validateEmail = useCallback((email: string): string => {
    if (!email) {
      return "";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return "Invalid email format.";
    }
    return "";
  }, []);

  const handleTeamMemberChange = useCallback((
    index: number,
    field: "email" | "role",
    value: string
  ) => {
    setTeamMembers((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // Validate email when email field changes
      if (field === "email") {
        const emailError = validateEmail(value);
        updated[index] = { ...updated[index], emailError };
      }
      
      return updated;
    });
  }, [validateEmail]);

  const handleAddTeamMember = useCallback(() => {
    setTeamMembers((prev) => [...prev, { email: "", role: "Team member", emailError: "" }]);
  }, []);

  const handleRemoveTeamMember = useCallback((index: number) => {
    setTeamMembers((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleCompleteAndNavigate = useCallback(async () => {
    if (isCompletingSetup) return;

    // Check for invalid emails before proceeding
    const invalidEmails = teamMembers.filter(m => m.email && validateEmail(m.email) !== "");
    if (invalidEmails.length > 0) {
      dispatch(
        setAlertMessageRequest({
          type: "error",
          message: "Please fix invalid email addresses before proceeding.",
        })
      );
      return;
    }

    try {
      setIsCompletingSetup(true);
      setPaymentError(null);
      setTeamMemberError(null);

      const { success, message } = await handleCompleteSetup();
      if (!success) {
        setPaymentError(message || "Failed to save payment information.");
        return;
      }

      const businessId = user?.business_id;

      if (businessId) {
        const membersToInvite = (teamMembers || [])
          .map((m) => ({
            email: (m.email || "").trim(),
            role: m.role === "Admin" ? "admin" : "standard",
          }))
          .filter((m) => {
            // Only invite members with valid emails
            const hasEmail = Boolean(m.email);
            const isValidEmail = validateEmail(m.email) === "";
            return hasEmail && isValidEmail;
          });

        try {
          for (const m of membersToInvite) {
            await axiosService.post(`/user/team/add/${businessId}`, {
              email: m.email,
              role: m.role,
            });
          }
        } catch (teamErr: any) {
          const teamMessage =
            teamErr?.data?.messages?.error ||
            teamErr?.response?.data?.messages?.error ||
            "Failed to invite team members. Please try again.";
          setTeamMemberError(teamMessage);
          return;
        }
      }

      setIsSuccessModalOpen(true);
    } catch (err: any) {
      const message =
        err?.data?.messages?.error ||
        err?.response?.data?.messages?.error ||
        "Failed to complete setup. Please try again.";
      dispatch(
        setAlertMessageRequest({
          type: "error",
          message,
        })
      );
    } finally {
      setIsCompletingSetup(false);
    }
  }, [dispatch, handleCompleteSetup, isCompletingSetup, teamMembers, validateEmail, user?.business_id]);

  const handleStartProject = useCallback(() => {
    setIsSuccessModalOpen(false);
    clearDraftStorage(user?.user_id);
    navigate("/customer/main");
  }, [navigate, user?.user_id]);

  const handlePaymentMethodChange = useCallback((method: string) => {
    setPaymentMethod(method);
  }, []);

  const handleSkip = useCallback(() => {
    navigate("/customer/dashboard");
  }, [navigate]);

  const stepComponents = useMemo<Record<StepKey, JSX.Element>>(
    () => ({
      1: (
        <VerifyEmail
          email={email}
          isVerifying={isVerifying}
          isLoggingIn={isLoggingIn}
          onNext={handleNext}
          isNextDisabled={!token || isVerifying}
          onBack={handleBack}
        />
      ),
      2: (
        <EmailVerified
          onContinue={handleContinue}
          onBack={handleBack}
          isContinueDisabled={!isBackendVerified || isVerifying || isAutoLoggingIn}
          isLoading={isVerifying || isAutoLoggingIn}
          loadingText={
            isAutoLoggingIn
              ? "Logging you in..."
              : "Verifying your email..."
          }
          infoMessage={verificationInfo}
          errorMessage={verificationError}
        />
      ),
      3: (
        <BusinessInfo
          businessName={businessName}
          industry={industry}
          onChangeBusinessName={setBusinessName}
          onChangeIndustry={setIndustry}
          onNext={handleNext}
          onBack={handleBack}
        />
      ),
      4: (
        <FinalizeAccount
          teamMembers={teamMembers}
          paymentMethod={paymentMethod}
          cardHolderName={cardHolderName}
          cardBrand={cardBrand}
          isCardNumberFocused={isCardNumberFocused}
          isCardExpiryFocused={isCardExpiryFocused}
          isCardCvcFocused={isCardCvcFocused}
          paymentError={paymentError}
          teamMemberError={teamMemberError}
          onTeamMemberChange={handleTeamMemberChange}
          onAddTeamMember={handleAddTeamMember}
          onRemoveTeamMember={handleRemoveTeamMember}
          onPaymentMethodChange={handlePaymentMethodChange}
          onChangeCardHolderName={setCardHolderName}
          onComplete={handleCompleteAndNavigate}
          onBack={handleBack}
          onSkip={handleSkip}
          isSavingCardInfo={isSavingCardInfo || isCompletingSetup}
        />
      ),
    }), [
      email,
      isVerifying,
      isLoggingIn,
      token,
      verificationError,
      verificationInfo,
      isBackendVerified,
      isAutoLoggingIn,
      handleNext,
      handleBack,
      handleContinue,
      businessName,
      industry,
      teamMembers,
      paymentMethod,
      cardHolderName,
      cardBrand,
      isCardNumberFocused,
      isCardExpiryFocused,
      isCardCvcFocused,
      paymentError,
      teamMemberError,
      handleTeamMemberChange,
      handleAddTeamMember,
      handleRemoveTeamMember,
      handlePaymentMethodChange,
      handleCompleteAndNavigate,
      handleSkip,
      isSavingCardInfo,
      isCompletingSetup,
      setCardHolderName,
    ]
  );

  return (
    <BusinessAuth>
      <>
        <Modal isOpen={isSuccessModalOpen} onClose={() => setIsSuccessModalOpen(false)} isCentered>
          <ModalOverlay bg="rgba(0,0,0,0.4)" />
          <ModalContent
            borderRadius="18px"
            maxW="548px"
            p={{ base: "24px", md: "32px" }}
          >
            <ModalCloseButton mt="14px" mr="14px" />
            <ModalBody p="0">
              <Flex direction="column" alignItems="center" textAlign="center">
                <Flex
                  w="64px"
                  h="64px"
                  borderRadius="full"
                  bg="#D6EFE1"
                  alignItems="center"
                  justifyContent="center"
                  mb="24px"
                >
                  <IconRoundedCheck boxSize="28px" color="#2B9057" />
                </Flex>

                <Text fontSize="24px" lineHeight="31px" fontWeight="600" color="#0B0E10" mb="12px">
                  Your account is created
                </Text>

                <Text fontSize="16px" lineHeight="21px" color="#505E70" mb="24px">
                  Your dedicated account manager will contact you shortly to answer any questions and help you get started.
                </Text>

                <Button
                  w="100%"
                  h="48px"
                  fontSize="16px"
                  bgColor="#3A7BFF"
                  fontWeight="600"
                  color="#FFFFFF"
                  borderRadius="28px"
                  border="1px solid #3A7BFF"
                  _hover={{
                    bgColor: "#FFFFFF",
                    color: "#3A7BFF",
                  }}
                  _active={{
                    bgColor: "#FFFFFF",
                    color: "#3A7BFF",
                  }}
                  onClick={handleStartProject}
                >
                  Start your project
                </Button>
              </Flex>
            </ModalBody>
          </ModalContent>
        </Modal>

        <Flex
          maxW="548px"
          w="100%"
          mx="auto"
          direction="column"
          alignItems="center"
        >
        <Box w="100%" mt="12px" mb="24px">
          <Progress
            value={progressPercentage}
            size="sm"
            borderRadius="4px"
            bgColor="#E9E9E9"
            w="548px"
            sx={{
              "& > div": {
                bgColor: "#3A7BFF",
              },
            }}
          />
        </Box>
        <Flex
          w="100%"
          bg={bgColor}
          borderRadius="12px"
          padding="40px 31px"
          direction="column"
          position="relative"
          border="1px solid #E9E9E9"
          fontFamily="General Sans, Inter, sans-serif"
        >
          {stepComponents[currentStep] ?? null}
        </Flex>
        </Flex>
      </>
    </BusinessAuth>
  );
}
